import ListingRelatedContent from '../features/singleListingNew/ListingRelatedContent.jsx';
import { publicListingCategory } from '@mts/shared/publicListingUrls';
import PublicBreadcrumbs from '../components/common/PublicBreadcrumbs.jsx';
import { publicBusinessLocationFacts, publicBusinessProfileFacts, publicBusinessImageFacts } from '../utils/listingSchemaFacts.js';
import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import { usePublicShareData } from '../hooks/usePublicShareData.js';
import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import BusinessTagsPanel from '../components/BusinessTagsPanel.jsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { Card, PublicContainer } from '@mts/ui';
import { BadgeCheck, CheckCircle2, ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import { useListing } from '../hooks/useListing';
import { useGuides } from '../hooks/useGuides';
import guidesApi from '../services/guides';
import {
  fetchGooglePlacePhotosForListing,
  fetchPortalListingEditorContract,
  getApiBaseUrl,
  importGooglePlacePhotoToListing,
  updatePortalListing,
  updatePortalListingEditorData,
  uploadListingGalleryPhoto,
} from '../services/api';
import { getListingOffers } from '../services/offersService';
import { fetchOwnProfile } from '../services/userProfiles';
import { trackVisitorEvent } from '../services/visitorTracking';
import { getTierKey } from '../utils/listingFeatures';
import { mapListingDetailOffers } from '../utils/listingDetailDeals';
import BasicTierSkeleton from '../features/singleListingNew/basic/BasicTierSkeleton';
import SingleListingSticky from '../features/singleListingNew/SingleListingSticky';
import { useAppOverlay } from '../components/overlay/AppOverlayProvider';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import NotFoundPage from './NotFoundPage.jsx';
import { buildBreadcrumbSchema, buildLocalBusinessSchema, buildWebPageSchema } from '../utils/structuredData.js';
import ListingCategoryEditor from '../components/listings/ListingCategoryEditor';
import ListingAddressEditor from '../components/listings/ListingAddressEditor';
import ListingPhoneEditor from '../components/listings/ListingPhoneEditor';
import ListingSocialController from '../features/singleListingNew/ListingSocialController.jsx';

const TIER_COMPONENTS = {
  basic: BasicTierSkeleton,
  featured: BasicTierSkeleton,
  spotlight: BasicTierSkeleton,
};

const GUIDE_FAB_TARGET_ID = 'guide-fab';

const BUSINESS_HOUR_DAYS = [
  { dayOfWeek: 0, label: 'Sunday' },
  { dayOfWeek: 1, label: 'Monday' },
  { dayOfWeek: 2, label: 'Tuesday' },
  { dayOfWeek: 3, label: 'Wednesday' },
  { dayOfWeek: 4, label: 'Thursday' },
  { dayOfWeek: 5, label: 'Friday' },
  { dayOfWeek: 6, label: 'Saturday' },
];

const PREVIEW_TIER_LABELS = {
  basic: 'Basic',
  featured: 'Featured',
  spotlight: 'Spotlight',
};

function normalizePreviewTier(value) {
  const tier = String(value || '').trim().toLowerCase();
  if (tier === 'basic' || tier === 'featured' || tier === 'spotlight') {
    return tier;
  }
  return null;
}

function normalizeGuideStepStatus(value) {
  const status = String(value || '').trim().toLowerCase();
  if (!status) {
    return '';
  }
  return status;
}

function getRoleKey(value) {
  return String(value?.role?.key || value?.role || '').trim().toLowerCase();
}

function isTenantOperatorRole(roleKey) {
  return [
    'tenant_owner',
    'tenant_admin',
    'tenant_staff',
    'system_admin',
    'system_staff',
  ].includes(String(roleKey || '').trim().toLowerCase());
}

function canEditListingFromProfile(profile, listing) {
  if (!profile || !listing) {
    return false;
  }

  const listingId = String(listing?.id || '').trim();
  const listingSlug = String(listing?.slug || '').trim();
  const listingTenantId = String(listing?.tenantId || listing?.tenant?.id || '').trim();

  const managedListings = Array.isArray(profile?.managedListings) ? profile.managedListings : [];
  const hasManagedListingAccess = managedListings.some((entry) => {
    const status = String(entry?.status || '').trim().toUpperCase();
    const nestedListing = entry?.listing || {};
    const managedListingId = String(nestedListing?.id || entry?.listingId || '').trim();
    const managedListingSlug = String(nestedListing?.slug || entry?.listingSlug || '').trim();
    const sameListing = (listingId && managedListingId === listingId) || (listingSlug && managedListingSlug === listingSlug);
    return sameListing && (!status || status === 'APPROVED');
  });
  if (hasManagedListingAccess) {
    return true;
  }

  const memberships = Array.isArray(profile?.memberships) ? profile.memberships : [];
  return memberships.some((membership) => {
    const roleKey = getRoleKey(membership);
    const membershipTenantId = String(membership?.tenantId || '').trim();
    return isTenantOperatorRole(roleKey) && listingTenantId && membershipTenantId === listingTenantId;
  });
}

function clampProgress(value) {
  const progress = Number(value);
  if (!Number.isFinite(progress)) return 0;
  return Math.max(0, Math.min(100, progress));
}

function countOutstandingGuideTasks(guide) {
  return getOutstandingGuideSteps(guide).length;
}

function getOutstandingGuideSteps(guide) {
  const steps = Array.isArray(guide?.steps) ? guide.steps : [];
  return steps.filter((step) => step?.status !== 'completed' && step?.status !== 'skipped');
}

function getGuideStepTargetId(step) {
  const actions = Array.isArray(step?.actions) ? step.actions : [];
  const actionWithTarget = actions.find((action) => action?.metadata?.targetId);
  return actionWithTarget?.metadata?.targetId || '';
}

function GuideFloatingActionButton({ progressPercent = 0, outstandingTaskCount = 0, open = false, buttonRef, onClick }) {
  const progress = clampProgress(progressPercent);
  const taskCount = Math.max(0, Number(outstandingTaskCount) || 0);
  const taskCountLabel = taskCount > 5 ? '5+' : String(taskCount);
  const radius = 29;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <div id={GUIDE_FAB_TARGET_ID} className="relative h-14 w-14 rounded-full sm:h-16 sm:w-16 md:h-[4.25rem] md:w-[4.25rem] lg:h-[4.5rem] lg:w-[4.5rem]">
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 64 64" aria-hidden="true">
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          strokeWidth="4"
          style={{ stroke: 'color-mix(in srgb, var(--primary) 18%, transparent)' }}
        />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="var(--spotlight)"
          strokeLinecap="round"
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="origin-center -rotate-90 transition-[stroke-dashoffset] duration-300 ease-out"
        />
      </svg>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`${open ? 'Close' : 'Open'} guide, ${progress}% complete${taskCount ? `, ${taskCount} outstanding task${taskCount === 1 ? '' : 's'}` : ''}`}
        aria-expanded={open}
        title={`${progress}% complete`}
        onClick={onClick}
        className="absolute inset-1 inline-flex items-center justify-center rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] shadow-lg shadow-black/20 transition-all hover:scale-105 hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--primary)]/30 active:scale-95"
      >
        <Lightbulb size={22} className="sm:h-6 sm:w-6 lg:h-7 lg:w-7" aria-hidden="true" />
      </button>
      {taskCount > 0 ? (
        <span
          className="absolute -right-1 -top-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-[var(--primary)] px-1.5 text-[11px] font-bold leading-none text-[var(--primary-foreground)] shadow-md sm:h-7 sm:min-w-7 sm:text-xs"
          aria-hidden="true"
        >
          {taskCountLabel}
        </span>
      ) : null}
    </div>
  );
}

function ListingNameController({ listing, guideInstanceId = '', stepKey = '', onSaved, onClose }) {
  const toast = useFeedback();
  const [draftValue, setDraftValue] = useState(String(listing?.name || ''));
  const [saving, setSaving] = useState(false);

  const trimmedValue = draftValue.trim();
  const originalValue = String(listing?.name || '').trim();
  const canSave = Boolean(trimmedValue) && trimmedValue !== originalValue && !saving;

  const save = async () => {
    if (!canSave || !listing?.id) return;

    try {
      setSaving(true);

      const updatedListing = await updatePortalListing(listing.id, { name: trimmedValue });
      if (guideInstanceId && stepKey) {
        await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
      }
      await onSaved?.({
        updatedListing,
        nextName: trimmedValue,
        guideInstanceId,
        stepKey,
      });
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Unable to save business name.', { id: 'action:ListingNameController:save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-name-controller-input">
        Business name
      </label>
      <textarea
        id="listing-name-controller-input"
        rows={3}
        value={draftValue}
        onChange={(event) => setDraftValue(event.target.value)}
        className="block w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
        autoFocus
      />

      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function ListingAboutController({ listing, guideInstanceId = '', stepKey = '', onSaved, onClose }) {
  const toast = useFeedback();
  const [draftValue, setDraftValue] = useState(String(listing?.description || ''));
  const [saving, setSaving] = useState(false);

  const trimmedValue = draftValue.trim();
  const originalValue = String(listing?.description || '').trim();
  const canSave = trimmedValue !== originalValue && !saving;

  const save = async () => {
    if (!canSave || !listing?.id) return;

    try {
      setSaving(true);

      const updatedListing = await updatePortalListing(listing.id, { description: trimmedValue });
      if (guideInstanceId && stepKey) {
        await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
      }
      await onSaved?.({
        updatedListing,
        nextDescription: trimmedValue,
        guideInstanceId,
        stepKey,
      });
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Unable to save About Us.', { id: 'action:ListingAboutController:save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex min-h-[38vh] flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-5">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-about-controller-input">
            About Us
          </label>
          <textarea
            id="listing-about-controller-input"
            rows={8}
            value={draftValue}
            onChange={(event) => {
              setDraftValue(event.target.value);

            }}
            placeholder="Describe what this business offers and what makes it stand out."
            className="block w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm leading-6 text-gray-950 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            autoFocus
          />
        </div>


      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save About Us'}
          </button>
        </div>
      </div>
    </form>
  );
}

function ListingContactController({ listing, guideInstanceId = '', stepKey = '', onSaved, onClose }) {
  const toast = useFeedback();
  const [values, setValues] = useState({
    phone: String(listing?.phone || listing?.phoneNumber || ''),
    websiteUrl: String(listing?.websiteUrl || listing?.website || ''),
    email: String(listing?.email || listing?.contactEmail || ''),
  });
  const [saving, setSaving] = useState(false);

  const originalValues = useMemo(() => ({
    phone: String(listing?.phone || listing?.phoneNumber || '').trim(),
    websiteUrl: String(listing?.websiteUrl || listing?.website || '').trim(),
    email: String(listing?.email || listing?.contactEmail || '').trim(),
  }), [listing]);
  const trimmedValues = {
    phone: values.phone.trim(),
    websiteUrl: values.websiteUrl.trim(),
    email: values.email.trim(),
  };
  const canSave = !saving && (
    trimmedValues.phone !== originalValues.phone
    || trimmedValues.websiteUrl !== originalValues.websiteUrl
    || trimmedValues.email !== originalValues.email
  );

  const updateValue = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));

  };

  const save = async () => {
    if (!canSave || !listing?.id) return;

    try {
      setSaving(true);

      const updatedListing = await updatePortalListing(listing.id, trimmedValues);
      if (guideInstanceId && stepKey) {
        await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
      }
      await onSaved?.({
        updatedListing,
        nextContact: trimmedValues,
        guideInstanceId,
        stepKey,
      });
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Unable to save contact details.', { id: 'action:ListingContactController:save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex min-h-[38vh] flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-5">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-contact-phone">
            Phone
          </label>
          <input
            id="listing-contact-phone"
            type="tel"
            value={values.phone}
            onChange={(event) => updateValue('phone', event.target.value)}
            placeholder="(555) 123-4567"
            className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-contact-website">
            Website
          </label>
          <input
            id="listing-contact-website"
            type="text"
            value={values.websiteUrl}
            onChange={(event) => updateValue('websiteUrl', event.target.value)}
            placeholder="https://example.com"
            className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-contact-email">
            Email
          </label>
          <input
            id="listing-contact-email"
            type="email"
            value={values.email}
            onChange={(event) => updateValue('email', event.target.value)}
            placeholder="hello@example.com"
            className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
          />
        </div>


      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Contact'}
          </button>
        </div>
      </div>
    </form>
  );
}

function normalizeBusinessHoursForEditor(listing) {
  const rows = Array.isArray(listing?.businessHours)
    ? listing.businessHours
    : Array.isArray(listing?.hours?.weekly)
      ? listing.hours.weekly
      : [];
  const byDay = new Map(rows
    .filter((row) => Number.isInteger(row?.dayOfWeek) && row.dayOfWeek >= 0 && row.dayOfWeek <= 6)
    .map((row) => [row.dayOfWeek, row]));

  return BUSINESS_HOUR_DAYS.map(({ dayOfWeek }) => {
    const row = byDay.get(dayOfWeek) || {};
    const openTime = typeof row.openTime === 'string' ? row.openTime.slice(0, 5) : '';
    const closeTime = typeof row.closeTime === 'string' ? row.closeTime.slice(0, 5) : '';
    return {
      dayOfWeek,
      openTime,
      closeTime,
      closed: !(openTime && closeTime),
    };
  });
}

function serializeBusinessHours(rows) {
  return rows.map((row) => ({
    dayOfWeek: row.dayOfWeek,
    openTime: row.closed ? null : row.openTime,
    closeTime: row.closed ? null : row.closeTime,
  }));
}

function ListingBusinessHoursController({ listing, guideInstanceId = '', stepKey = '', onSaved, onClose }) {
  const toast = useFeedback();
  const [rows, setRows] = useState(() => normalizeBusinessHoursForEditor(listing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const originalRows = useMemo(() => serializeBusinessHours(normalizeBusinessHoursForEditor(listing)), [listing]);
  const nextRows = useMemo(() => serializeBusinessHours(rows), [rows]);
  const canSave = !saving && JSON.stringify(nextRows) !== JSON.stringify(originalRows);

  const updateRow = (dayOfWeek, patch) => {
    setRows((current) => current.map((row) => {
      if (row.dayOfWeek !== dayOfWeek) return row;
      const next = { ...row, ...patch };
      if (patch.closed === false && (!next.openTime || !next.closeTime)) {
        next.openTime = next.openTime || '09:00';
        next.closeTime = next.closeTime || '17:00';
      }
      return next;
    }));
    setError('');
  };

  const validateRows = () => {
    for (const row of rows) {
      if (row.closed) continue;
      if (!row.openTime || !row.closeTime) {
        return 'Open days need both an open and close time.';
      }
      if (row.openTime >= row.closeTime) {
        const dayLabel = BUSINESS_HOUR_DAYS.find((day) => day.dayOfWeek === row.dayOfWeek)?.label || 'Each day';
        return `${dayLabel} close time must be after open time.`;
      }
    }
    return '';
  };

  const save = async () => {
    if (!canSave || !listing?.id) return;

    const validationError = validateRows();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError('');
      const updatedListing = await updatePortalListing(listing.id, { businessHours: nextRows });
      if (guideInstanceId && stepKey) {
        await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
      }
      await onSaved?.({
        updatedListing,
        nextBusinessHours: nextRows,
        guideInstanceId,
        stepKey,
      });
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Unable to save business hours.', { id: 'action:ListingBusinessHoursController:save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex min-h-[38vh] flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-5">
        {BUSINESS_HOUR_DAYS.map(({ dayOfWeek, label }) => {
          const row = rows.find((item) => item.dayOfWeek === dayOfWeek) || {};
          const closed = Boolean(row.closed);

          return (
            <div
              key={dayOfWeek}
              className="rounded-md border border-gray-200 bg-white p-3"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-gray-900">{label}</span>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={closed}
                    onChange={(event) => updateRow(dayOfWeek, { closed: event.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                  Closed
                </label>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <label className="space-y-1 text-xs font-semibold uppercase text-gray-500">
                  Open
                  <input
                    type="time"
                    value={row.openTime || ''}
                    onChange={(event) => updateRow(dayOfWeek, { openTime: event.target.value })}
                    disabled={closed}
                    className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
                  />
                </label>
                <label className="space-y-1 text-xs font-semibold uppercase text-gray-500">
                  Close
                  <input
                    type="time"
                    value={row.closeTime || ''}
                    onChange={(event) => updateRow(dayOfWeek, { closeTime: event.target.value })}
                    disabled={closed}
                    className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
                  />
                </label>
              </div>
            </div>
          );
        })}

        {error ? (
          <InlineFeedback variant="error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {error}
          </InlineFeedback>
        ) : null}
      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Hours'}
          </button>
        </div>
      </div>
    </form>
  );
}

function getListingGooglePlaceId(listing) {
  const meta = listing?.metaJson && typeof listing.metaJson === 'object' && !Array.isArray(listing.metaJson)
    ? listing.metaJson
    : {};
  return String(listing?.googlePlaceId || meta.googlePlaceId || meta.placeId || '').trim();
}

function resolveMediaAssetUrl(asset) {
  const rawUrl = String(asset?.publicUrl || asset?.url || '').trim();
  if (!rawUrl) return '';
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith('/')) return `${getApiBaseUrl()}${rawUrl}`;
  return rawUrl;
}

function ListingPhotosController({ listing, guideInstanceId = '', stepKey = '', onSaved, onClose }) {
  const toast = useFeedback();
  const [mode, setMode] = useState('upload');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [googlePhotos, setGooglePhotos] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [importingPhotoName, setImportingPhotoName] = useState('');
  const tenantId = String(listing?.tenantId || listing?.tenant?.id || '').trim();
  const googlePlaceId = getListingGooglePlaceId(listing);
  const canFetchGooglePhotos = Boolean(tenantId && googlePlaceId && listing?.id);

  const markGuideComplete = async () => {
    if (guideInstanceId && stepKey) {
      await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
    }
  };

  const saveUploadedFiles = async () => {
    if (!listing?.id || selectedFiles.length === 0) return;

    try {
      setSaving(true);
      setError('');

      const uploadedAssets = [];
      for (const file of selectedFiles) {
        const asset = await uploadListingGalleryPhoto(listing.id, file, {
          altText: listing?.name ? `${listing.name} photo` : 'Listing photo',
        });
        uploadedAssets.push(asset);
      }
      await markGuideComplete();
      await onSaved?.({ addedAssets: uploadedAssets, guideInstanceId, stepKey });
      setSelectedFiles([]);
      toast.success(`${uploadedAssets.length} photo${uploadedAssets.length === 1 ? '' : 's'} uploaded.`, { id: 'action:ListingPhotosController:saveUploadedFiles' + ':' + (tenantId || '') });
    } catch (err) {
      toast.error(err?.message || 'Unable to upload photos.', { id: 'action:ListingPhotosController:saveUploadedFiles' + ':' + (tenantId || '') });
    } finally {
      setSaving(false);
    }
  };

  const fetchGooglePhotos = async () => {
    if (!canFetchGooglePhotos) return;

    try {
      setGoogleLoading(true);
      setError('');

      const payload = await fetchGooglePlacePhotosForListing(tenantId, googlePlaceId, listing.id);
      setGooglePhotos(Array.isArray(payload?.photos) ? payload.photos : []);
    } catch (err) {
      setError(err?.message || 'Unable to fetch Google photos.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const importGooglePhoto = async (photo) => {
    if (!photo?.photoName || !tenantId || !listing?.id) return;

    try {
      setImportingPhotoName(photo.photoName);
      setError('');

      const payload = await importGooglePlacePhotoToListing(tenantId, listing.id, photo.photoName);
      const asset = payload?.asset || payload;
      await markGuideComplete();
      await onSaved?.({ addedAssets: [asset], guideInstanceId, stepKey });
      toast.success('Google photo imported.', { id: 'action:ListingPhotosController:importGooglePhoto' + ':' + (tenantId || '') });
    } catch (err) {
      toast.error(err?.message || 'Unable to import Google photo.', { id: 'action:ListingPhotosController:importGooglePhoto' + ':' + (tenantId || '') });
    } finally {
      setImportingPhotoName('');
    }
  };

  return (
    <div className="flex min-h-[42vh] flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-5">
        <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`h-9 rounded px-3 text-sm font-semibold transition-colors ${mode === 'upload' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-600 hover:text-gray-950'}`}
          >
            Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('google')}
            className={`h-9 rounded px-3 text-sm font-semibold transition-colors ${mode === 'google' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-600 hover:text-gray-950'}`}
          >
            Google
          </button>
        </div>

        {mode === 'upload' ? (
          <div className="space-y-4">
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-gray-900">Photos</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                onChange={(event) => {
                  setSelectedFiles(Array.from(event.target.files || []));
                  setError('');

                }}
                className="block w-full text-sm text-gray-700 file:mr-3 file:h-9 file:rounded-md file:border-0 file:bg-[var(--primary)] file:px-3 file:text-sm file:font-semibold file:text-[var(--primary-foreground)] hover:file:bg-[var(--primary-hover)]"
              />
            </label>

            {selectedFiles.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {selectedFiles.map((file) => (
                  <div key={`${file.name}-${file.size}`} className="truncate rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                    {file.name}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              onClick={fetchGooglePhotos}
              disabled={!canFetchGooglePhotos || googleLoading}
              className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {googleLoading ? 'Loading...' : 'Load Google Photos'}
            </button>

            {!canFetchGooglePhotos ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                Google photos are not available for this listing.
              </p>
            ) : null}

            {googlePhotos.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {googlePhotos.map((photo) => (
                  <figure key={photo.photoName} className="overflow-hidden rounded-md border border-gray-200 bg-white">
                    <img
                      src={photo.mediaUrl}
                      alt="Google listing photo"
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                    <figcaption className="p-2">
                      <button
                        type="button"
                        onClick={() => importGooglePhoto(photo)}
                        disabled={Boolean(importingPhotoName)}
                        className="h-8 w-full rounded-md bg-[var(--primary)] px-2 text-xs font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {importingPhotoName === photo.photoName ? 'Importing...' : 'Import'}
                      </button>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {error ? (
          <InlineFeedback variant="error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {error}
          </InlineFeedback>
        ) : null}

      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || Boolean(importingPhotoName)}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Done
          </button>
          {mode === 'upload' ? (
            <button
              type="button"
              onClick={saveUploadedFiles}
              disabled={saving || selectedFiles.length === 0}
              className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Uploading...' : 'Upload Photos'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function getListingCategorySelection(listing) {
  const assignedCategories = Array.isArray(listing?.listingCategories) ? listing.listingCategories : [];
  const tenantCategories = assignedCategories
    .map((entry) => entry?.tenantCategory)
    .filter(Boolean);
  const primaryCategory = tenantCategories.find((category) => !category?.parentId) || tenantCategories[0] || null;
  const secondaryCategoryIds = tenantCategories
    .filter((category) => category?.id && category?.parentId && (!primaryCategory?.id || category.parentId === primaryCategory.id))
    .map((category) => category.id);

  return {
    primaryCategoryId: primaryCategory?.id || '',
    secondaryCategoryIds,
  };
}

function ListingGuidePanelContent({ guide, loading, error, onRefresh, onOpenFieldController }) {
  const [updatingStepKey, setUpdatingStepKey] = useState('');
  const [runningStepActionKey, setRunningStepActionKey] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [selectedStepKey, setSelectedStepKey] = useState('');
  const stepTargetDriverRef = useRef(null);
  const guideSteps = Array.isArray(guide?.steps) ? guide.steps : [];
  const outstandingSteps = getOutstandingGuideSteps(guide);
  const nextAvailableStep = outstandingSteps[0] || null;
  const selectedStep = guideSteps.find((step) => step?.key === selectedStepKey) || null;
  const displayedStep = selectedStep || nextAvailableStep || guideSteps[0] || null;
  const displayedStepIndex = displayedStep
    ? guideSteps.findIndex((step) => step?.key === displayedStep.key)
    : -1;
  const canGoToPreviousStep = displayedStepIndex > 0;
  const canGoToNextStep = displayedStepIndex >= 0 && displayedStepIndex < guideSteps.length - 1;
  const isStepSelected = Boolean(selectedStep);
  const displayedStepStatus = normalizeGuideStepStatus(displayedStep?.status);
  const displayedStepCompleted = displayedStepStatus === 'completed';
  const displayedStepSkipped = displayedStepStatus === 'skipped';
  const remainingStepCount = outstandingSteps.length;
  const displayedStepTargetId = getGuideStepTargetId(displayedStep);

  useEffect(() => () => {
    stepTargetDriverRef.current?.destroy();
    stepTargetDriverRef.current = null;
  }, []);

  useEffect(() => {
    setShowAllSteps(false);
    setSelectedStepKey('');
  }, [guide?.id]);

  const goToStepByIndex = (index) => {
    const step = guideSteps[index];
    if (!step?.key) return;
    setSelectedStepKey(step.key);
  };

  const spotlightStepTarget = (targetId, { scroll = true, delayMs = 0 } = {}) => {
    if (!targetId || typeof document === 'undefined') return;

    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      if (scroll) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      stepTargetDriverRef.current?.destroy();
      stepTargetDriverRef.current = driver({
        animate: true,
        allowClose: true,
        showButtons: [],
        stagePadding: 8,
        stageRadius: 8,
        zIndex: 19990,
      });
      stepTargetDriverRef.current.highlight({
        element: target,
      });
    }, delayMs);
  };

  useEffect(() => {
    if (loading || !displayedStepTargetId) return;
    spotlightStepTarget(displayedStepTargetId, { scroll: true, delayMs: 250 });
  }, [displayedStep?.key, displayedStepTargetId, loading]);

  const completeStep = async (stepKey) => {
    if (!guide?.id || !stepKey) return;

    try {
      const completedStepIndex = guideSteps.findIndex((step) => step?.key === stepKey);
      const nextIncompleteStep = guideSteps
        .slice(Math.max(0, completedStepIndex + 1))
        .find((step) => step?.status !== 'completed' && step?.status !== 'skipped');

      setUpdatingStepKey(stepKey);
      await guidesApi.updateStepStatus(guide.id, stepKey, 'completed');
      if (nextIncompleteStep?.key) {
        setSelectedStepKey(nextIncompleteStep.key);
      }
      await onRefresh?.();
    } finally {
      setUpdatingStepKey('');
    }
  };

  const executeStepAction = async (step, action) => {
    if (!guide?.id || !step?.key || !action?.key) return;

    try {
      setRunningStepActionKey(`${step.key}:${action.key}`);
      const response = await guidesApi.executeStepAction(guide.id, step.key, action.key);
      const targetId = response?.result?.action?.target?.targetId || action?.metadata?.targetId;
      const fieldKey = response?.result?.action?.target?.fieldKey || action?.metadata?.fieldKey || '';
      const actionKey = response?.result?.action?.actionKey || action?.actionKey || '';
      if (
        actionKey === 'listing.name.edit'
        || fieldKey === 'listing.name'
        || actionKey === 'listing.category.edit'
        || fieldKey === 'listing.category'
        || actionKey === 'listing.address.edit'
        || fieldKey === 'listing.address'
        || actionKey === 'listing.phone.edit'
        || fieldKey === 'listing.phone'
        || actionKey === 'listing.website.edit'
        || fieldKey === 'listing.website'
        || fieldKey === 'listing.websiteUrl'
        || actionKey === 'listing.contact.edit'
        || fieldKey === 'listing.contact'
        || actionKey === 'listing.social.edit'
        || fieldKey === 'listing.social'
        || fieldKey === 'listing.socialLinks'
        || actionKey === 'listing.description.edit'
        || fieldKey === 'listing.description'
        || actionKey === 'listing.hours.edit'
        || fieldKey === 'listing.hours'
        || fieldKey === 'listing.businessHours'
        || actionKey === 'listing.photos.add'
        || fieldKey === 'listing.photos'
        || fieldKey === 'listing.galleryImages'
      ) {
        onOpenFieldController?.({ targetId, fieldKey, actionKey, guideInstanceId: guide.id, stepKey: step.key });
        return;
      }
      if (targetId) {
        spotlightStepTarget(targetId, { scroll: true, delayMs: 350 });
      }
    } finally {
      setRunningStepActionKey('');
    }
  };

  const dismiss = async () => {
    if (!guide?.id) return;

    try {
      setDismissing(true);
      await guidesApi.dismissGuide(guide.id);
      await onRefresh?.();
    } finally {
      setDismissing(false);
    }
  };

  if (!loading && !guide && !error) {
    return null;
  }

  return (
    <>
          {loading ? (
            <div className="mt-3 space-y-2">
              <div className="h-2 rounded bg-gray-100" />
              <div className="h-10 rounded-lg bg-gray-50" />
            </div>
          ) : error && !guide ? (
            <InlineFeedback variant="error" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{error}</InlineFeedback>
          ) : (
            <>
              {guide?.description ? (
                <p className="mt-1 text-sm leading-6 text-gray-600">{guide.description}</p>
              ) : null}

              <div className="mt-3 flex items-center gap-3">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-[var(--primary)]"
                    style={{ width: `${Math.max(0, Math.min(100, guide?.progressPercent || 0))}%` }}
                  />
                </div>
                <span className="shrink-0 text-xs font-semibold text-[var(--primary)]">
                  {guide?.progressPercent || 0}%
                </span>
              </div>

              <div className="mt-4">
                {displayedStep ? (
                  <div className="rounded-lg border border-[var(--primary)] px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)]">
                          {isStepSelected ? 'Selected Step' : 'Next Step'}
                        </p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <CheckCircle2
                            size={15}
                            className={`shrink-0 ${displayedStepCompleted ? 'text-emerald-600' : 'text-gray-300'}`}
                            aria-hidden="true"
                          />
                          <span className="text-sm font-semibold leading-tight text-gray-900">{displayedStep.title}</span>
                        </div>
                        {displayedStep.description ? (
                          <p className="mt-0.5 text-xs leading-4 text-gray-500">{displayedStep.description}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          aria-label="Previous guide step"
                          title="Previous step"
                          onClick={() => goToStepByIndex(displayedStepIndex - 1)}
                          disabled={!canGoToPreviousStep}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <ChevronLeft size={16} aria-hidden="true" />
                        </button>
                        <span className="min-w-[3.25rem] text-center text-[11px] font-semibold text-gray-500">
                          {displayedStepIndex >= 0 ? `${displayedStepIndex + 1} of ${guideSteps.length}` : '0 of 0'}
                        </span>
                        <button
                          type="button"
                          aria-label="Next guide step"
                          title="Next step"
                          onClick={() => goToStepByIndex(displayedStepIndex + 1)}
                          disabled={!canGoToNextStep}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <ChevronRight size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      {Array.isArray(displayedStep.actions) && displayedStep.actions.length > 0 ? (
                        <>
                        {displayedStep.actions.map((action) => (
                          <button
                            key={action.key}
                            type="button"
                            onClick={() => executeStepAction(displayedStep, action)}
                            disabled={runningStepActionKey === `${displayedStep.key}:${action.key}`}
                            className="h-8 rounded-md border border-[color:color-mix(in_srgb,var(--primary)_36%,transparent)] bg-transparent px-3 text-xs font-semibold text-[var(--primary)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--primary)_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {action.label || 'Open'}
                          </button>
                        ))}
                        </>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => completeStep(displayedStep.key)}
                        disabled={displayedStepCompleted || displayedStepSkipped || updatingStepKey === displayedStep.key}
                        className="h-8 rounded-md border border-gray-200 px-2.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {displayedStepCompleted ? 'Done' : displayedStepSkipped ? 'Skipped' : 'Done'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <p className="text-sm font-semibold text-emerald-800">All guide steps are complete.</p>
                    </div>
                  </div>
                )}
              </div>

              {showAllSteps ? (
                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                  {guideSteps.map((step) => {
                    const status = normalizeGuideStepStatus(step?.status);
                    const isCompleted = status === 'completed';
                    const isSkipped = status === 'skipped';
                    const isCurrent = nextAvailableStep?.key === step?.key;
                    const isSelected = displayedStep?.key === step?.key;
                    const statusLabel = isCompleted
                      ? 'Done'
                      : isSkipped
                        ? 'Skipped'
                        : isCurrent
                          ? 'Next'
                          : 'Pending';

                    return (
                      <button
                        key={step.key}
                        type="button"
                        onClick={() => setSelectedStepKey(step.key)}
                        className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
                          isSelected
                            ? 'border-[var(--primary)] bg-[color:color-mix(in_srgb,var(--primary)_5%,white)]'
                            : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <CheckCircle2
                          size={15}
                          className={`shrink-0 ${
                            isCompleted
                              ? 'text-emerald-600'
                              : isCurrent
                                ? 'text-[var(--primary)]'
                                : 'text-gray-300'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-800">
                          {step.title}
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            isCompleted
                              ? 'bg-emerald-50 text-emerald-700'
                              : isCurrent
                                ? 'bg-[color:color-mix(in_srgb,var(--primary)_10%,white)] text-[var(--primary)]'
                                : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setShowAllSteps((value) => !value)}
                  className="text-xs font-semibold text-[var(--primary)] hover:underline disabled:cursor-default disabled:text-gray-400 disabled:no-underline"
                  disabled={remainingStepCount === 0}
                  aria-expanded={showAllSteps}
                >
                  {showAllSteps
                    ? 'Hide steps'
                    : remainingStepCount === 1
                      ? '1 step remaining'
                      : `${remainingStepCount} steps remaining`}
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  disabled={dismissing}
                  className="text-xs font-semibold text-gray-500 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Dismiss
                </button>
              </div>
            </>
          )}
    </>
  );
}

function ListingGuidePanel({ guide, loading, error, onRefresh, onOpenFieldController }) {
  const buttonRef = useRef(null);
  const [showHighlightActions, setShowHighlightActions] = useState(false);
  const [highlightActionBusy, setHighlightActionBusy] = useState('');
  const [restoreFabAfterOverlayClose, setRestoreFabAfterOverlayClose] = useState(false);
  const fabHighlightDriverRef = useRef(null);
  const fabHighlightShownForRef = useRef('');
  const fabHiddenByOverlayRef = useRef(false);
  const { activeOverlay, openOverlay, closeOverlay, isFabBlocked } = useAppOverlay();
  const isGuideOverlayOpen = activeOverlay?.key === 'guide-hub';
  const isGuideFabBlockedByOverlay = isFabBlocked && !isGuideOverlayOpen;
  const highlightConfig = guide?.ui?.highlight || null;

  const openGuideOverlay = useCallback(() => {
    openOverlay({
      key: 'guide-hub',
      title: loading ? 'Listing guide' : (guide?.title || 'Listing guide'),
      description: guide?.description || 'Use guides to complete the next best steps for this listing.',
      desktopPresentation: 'floatingCallout',
      anchorRef: buttonRef,
      priority: 'low',
      blocksFab: false,
      closeOnRouteChange: true,
      content: (
        <ListingGuidePanelContent
          guide={guide}
          loading={loading}
          error={error}
          onRefresh={onRefresh}
          onOpenFieldController={onOpenFieldController}
        />
      ),
    });
  }, [error, guide, loading, onOpenFieldController, onRefresh, openOverlay]);

  useEffect(() => {
    if (isGuideOverlayOpen) {
      openGuideOverlay();
    }
  }, [isGuideOverlayOpen, openGuideOverlay]);

  useEffect(() => () => {
    fabHighlightDriverRef.current?.destroy();
    fabHighlightDriverRef.current = null;
  }, []);

  useEffect(() => {
    if (isGuideFabBlockedByOverlay) {
      fabHiddenByOverlayRef.current = true;
      setRestoreFabAfterOverlayClose(false);
      return;
    }

    if (fabHiddenByOverlayRef.current && !isFabBlocked) {
      fabHiddenByOverlayRef.current = false;
      setRestoreFabAfterOverlayClose(true);
    }
  }, [isFabBlocked, isGuideFabBlockedByOverlay]);

  const destroyFabHighlight = () => {
    fabHighlightDriverRef.current?.destroy();
    setShowHighlightActions(false);
  };

  const startGuideFromHighlight = async () => {
    if (!guide?.id) return;
    try {
      setHighlightActionBusy('start');
      destroyFabHighlight();
      openGuideOverlay();
      await guidesApi.startGuide(guide.id);
      await onRefresh?.();
    } finally {
      setHighlightActionBusy('');
    }
  };

  const dismissHighlight = () => {
    destroyFabHighlight();
  };

  const remindMeLaterFromHighlight = async () => {
    if (!guide?.id) return;
    try {
      setHighlightActionBusy('snooze');
      destroyFabHighlight();
      await guidesApi.snoozeGuide(guide.id);
      await onRefresh?.();
    } finally {
      setHighlightActionBusy('');
    }
  };

  const highlightFab = () => {
    const guideId = String(guide?.id || '');
    const target = document.getElementById(GUIDE_FAB_TARGET_ID);
    if (!target || !guideId || fabHighlightShownForRef.current === guideId) return false;

    fabHighlightDriverRef.current?.destroy();
    fabHighlightDriverRef.current = driver({
      animate: true,
      allowClose: true,
      showButtons: [],
      stagePadding: 8,
      stageRadius: 999,
      zIndex: 20000,
    });

    fabHighlightDriverRef.current.highlight({
      element: `#${GUIDE_FAB_TARGET_ID}`,
    });
    fabHighlightShownForRef.current = guideId;
    setShowHighlightActions(true);
    return true;
  };

  useEffect(() => {
    if (
      loading
      || highlightConfig?.target !== GUIDE_FAB_TARGET_ID
      || fabHighlightShownForRef.current === guide?.id
    ) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let timeoutId = null;

    const tryHighlight = () => {
      if (cancelled || fabHighlightShownForRef.current === guide?.id) return;
      if (highlightFab()) return;

      attempts += 1;
      if (attempts < 8) {
        timeoutId = window.setTimeout(tryHighlight, 120);
      }
    };

    timeoutId = window.setTimeout(tryHighlight, 0);

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [guide?.id, highlightConfig?.target, highlightConfig?.title, highlightConfig?.description, loading]);

  const handleFabClick = () => {
    if (isGuideOverlayOpen) {
      closeOverlay();
      return;
    }

    destroyFabHighlight();
    openGuideOverlay();
  };

  if (!loading && !guide && !error) {
    return null;
  }

  const highlightCallout = showHighlightActions && highlightConfig?.target === GUIDE_FAB_TARGET_ID && typeof document !== 'undefined'
    ? createPortal(
      <div className="mts-guide-highlight-callout fixed bottom-[13rem] right-4 z-[2147483647] max-w-[calc(100vw-2rem)] sm:bottom-28 sm:right-6 md:bottom-32 md:right-8">
        <div className="relative w-[min(24rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-4 text-left shadow-xl">
          <span className="absolute -bottom-2 right-8 h-4 w-4 rotate-45 border-b border-r border-gray-200 bg-white" aria-hidden="true" />
          <h2 className="text-base font-semibold text-gray-950">{highlightConfig?.title || guide?.title || 'Guide'}</h2>
          {(highlightConfig?.description || guide?.description) ? (
            <p className="mt-2 text-sm leading-6 text-gray-700">
              {highlightConfig?.description || guide?.description}
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={startGuideFromHighlight}
              disabled={Boolean(highlightActionBusy)}
              className="h-8 rounded-md border border-[color:color-mix(in_srgb,var(--primary)_36%,transparent)] bg-transparent px-3 text-xs font-semibold text-[var(--primary)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--primary)_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Start
            </button>
            <button
              type="button"
              onClick={dismissHighlight}
              disabled={Boolean(highlightActionBusy)}
              className="h-8 rounded-md border border-[color:color-mix(in_srgb,var(--primary)_36%,transparent)] bg-transparent px-3 text-xs font-semibold text-[var(--primary)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--primary)_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={remindMeLaterFromHighlight}
              disabled={Boolean(highlightActionBusy)}
              className="h-8 rounded-md border border-[color:color-mix(in_srgb,var(--primary)_36%,transparent)] bg-transparent px-3 text-xs font-semibold text-[var(--primary)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--primary)_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Remind Me Later
            </button>
          </div>
        </div>
      </div>,
      document.body,
    )
    : null;

  if (isGuideFabBlockedByOverlay && !restoreFabAfterOverlayClose) {
    return highlightCallout;
  }

  return (
    <>
      <div className="fixed bottom-[8.5rem] right-4 z-[20010] max-w-[calc(100vw-2rem)] sm:bottom-6 sm:right-6 md:right-8">

        <GuideFloatingActionButton
          buttonRef={buttonRef}
          progressPercent={guide?.progressPercent || 0}
          outstandingTaskCount={countOutstandingGuideTasks(guide)}
          open={isGuideOverlayOpen}
          onClick={handleFabClick}
        />
      </div>
      {highlightCallout}
    </>
  );
}

export default function SingleListingNewPage() {
  const { categorySlug, slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedPreview = searchParams.get('embed') === 'listing';
  const isEditorEmbed = isEmbedPreview && searchParams.get('editor') === '1';
  const isNavigationLocked = searchParams.get('lockNavigation') === '1';
  const previewTier = normalizePreviewTier(searchParams.get('previewTier'));
  const guideStepKey = String(searchParams.get('guideStepKey') || '').trim().toLowerCase();
  const guideStepStatus = normalizeGuideStepStatus(searchParams.get('guideStepStatus'));
  const { listing, setListing, loading, error } = useListing(slug, { categorySlug, isEmbedPreview });
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const { openOverlay, closeOverlay } = useAppOverlay();
  const [profile, setProfile] = useState(null);
  const [editorCapability, setEditorCapability] = useState({
    loading: false,
    allowed: false,
    error: '',
  });
  const editorListingRef = useRef('');
  const editorUpdateReadyRef = useRef(false);
  const trackedViewRef = useRef('');
  const initialOffers = useInitialPublicData("listingOffers");
  const [listingOffersState, setListingOffersState] = useState({
    offers: initialOffers || [],
    loading: false,
    error: '',
  });

  const publicContent = useMemo(() => {
    if (!listing) return null;
    const publicPath = String(listing.publicPath || listing.publicUrl || '').trim();
    const fallbackPath = categorySlug
      ? `/listings/${encodeURIComponent(categorySlug)}/${encodeURIComponent(listing.slug || slug)}`
      : `/listings/${encodeURIComponent(listing.slug || slug)}`;
    const path = publicPath.startsWith('/') ? publicPath : fallbackPath;
    const city = listing.city;
    const assignedCategory = publicListingCategory(listing);
    const category = assignedCategory?.name || '';
    const breadcrumbs = [{ name: 'Home', href: '/' }, { name: 'Business Directory', href: '/listings' }, ...(category && assignedCategory.isVisible !== false ? [{ name: category, href: `/listings?category=${encodeURIComponent(assignedCategory.slug)}` }] : []), { name: listing.name }];
    const description = listing.seoDescription || listing.description || `View available contact information, services and local updates for ${listing.name}.`;
    const { imageUrl, images } = publicBusinessImageFacts(listing, tenant?.canonicalOrigin);
    const metadata = buildPublicPageMetadata({ tenant, pathname: path, canonicalPath: path, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: `${listing.name}${city ? ` in ${city}` : ''} | ${spotlightName}`, description, imageUrl, imageAlt: `${listing.name}${category ? ` — ${category}` : ''}` });
    return { metadata, path, city, category, breadcrumbs, description, imageUrl, images, locationFacts: publicBusinessLocationFacts(listing) };
  }, [listing, categorySlug, slug, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);
  const shareData = usePublicShareData({ contentType: 'listing', title: listing?.name, metadata: publicContent?.metadata, status: listing?.status, publicationStatus: listing?.publicationStatus, hidden: isEmbedPreview || isEditorEmbed || Boolean(previewTier) });

  usePublicMetadata(() => {
    if (loading) return;
    if (error?.status === 404 || (!error && !listing)) {
      return (buildPublicPageMetadata({ tenant, pathname: `/listings/${encodeURIComponent(slug)}`, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: `Listing not found | ${spotlightName}`, description: 'The requested business listing is not available.', robots: 'noindex,follow', jsonLd: [] }));
    }
    if (isEmbedPreview || previewTier) {
      return buildPublicPageMetadata({ tenant, pathname: `/listings/${encodeURIComponent(slug)}`, robots: 'noindex,follow', jsonLd: [] });
    }
    const { metadata, breadcrumbs, images, locationFacts } = publicContent;
    metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: metadata.title, description: metadata.description }), buildLocalBusinessSchema({ url: metadata.canonicalUrl, name: listing.name, description: metadata.description, images, telephone: listing.phone || listing.phoneNumber, ...locationFacts, ...publicBusinessProfileFacts(listing) }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: breadcrumbs.map(item => ({ name: item.name, url: item.href ? `${tenant.canonicalOrigin}${item.href}` : metadata.canonicalUrl })) })];
    return (metadata);
  }, [publicContent, categorySlug, error, isEmbedPreview, listing, loading, previewTier, slug, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  const isVerifyContactInfoGuideActive = isEmbedPreview
    && guideStepKey === 'verify_contact_info'
    && (guideStepStatus === 'in_progress' || guideStepStatus === 'started');

  useEffect(() => {
    if (isEmbedPreview || !listing?.id || trackedViewRef.current === listing.id) return;
    trackedViewRef.current = listing.id;
    trackVisitorEvent({
      eventType: 'listing.viewed',
      listingId: listing.id,
      metadata: { slug: listing.slug, tier: listing.tier },
    });
  }, [isEmbedPreview, listing?.id, listing?.slug, listing?.tier]);

  useEffect(() => {
    if (isEmbedPreview && !isEditorEmbed) {
      setProfile(null);
      return undefined;
    }

    let ignore = false;
    fetchOwnProfile()
      .then((payload) => {
        if (!ignore) setProfile(payload?.profile || null);
      })
      .catch(() => {
        if (!ignore) setProfile(null);
      });

    return () => {
      ignore = true;
    };
  }, [isEditorEmbed, isEmbedPreview]);

  useEffect(() => {
    if (!isEditorEmbed || !listing?.id) {
      setEditorCapability({ loading: false, allowed: false, error: '' });
      return undefined;
    }

    let ignore = false;
    setEditorCapability({ loading: true, allowed: false, error: '' });
    fetchPortalListingEditorContract(listing.id)
      .then(() => {
        if (!ignore) setEditorCapability({ loading: false, allowed: true, error: '' });
      })
      .catch((capabilityError) => {
        if (ignore) return;
        const message = capabilityError?.status === 401
          ? 'Your session has expired. Sign in again to edit this listing.'
          : capabilityError?.status === 403
            ? 'You do not have permission to edit this listing.'
            : 'Editing is unavailable right now. Refresh the editor to try again.';
        setEditorCapability({ loading: false, allowed: false, error: message });
      });

    return () => {
      ignore = true;
    };
  }, [isEditorEmbed, listing?.id]);

  useEffect(() => {
    if (!isEditorEmbed || !listing?.id) return;

    if (editorListingRef.current !== listing.id) {
      editorListingRef.current = listing.id;
      editorUpdateReadyRef.current = true;
      return;
    }

    if (!editorUpdateReadyRef.current) {
      editorUpdateReadyRef.current = true;
      return;
    }

    if (window.parent === window) return;
    let targetOrigin = window.location.origin;
    try {
      if (document.referrer) targetOrigin = new URL(document.referrer).origin;
    } catch {
      targetOrigin = window.location.origin;
    }
    window.parent.postMessage({
      type: 'mts.listing.updated',
      listingId: listing.id,
      section: 'listing',
    }, targetOrigin);
  }, [isEditorEmbed, listing]);

  useEffect(() => {
    if (initialOffers) { setListingOffersState({ offers: initialOffers, loading: false, error: '' }); return; }
    const listingId = String(listing?.id || '').trim();
    if (!listingId) {
      setListingOffersState({ offers: [], loading: false, error: '' });
      return undefined;
    }

    let ignore = false;
    setListingOffersState((current) => ({
      ...current,
      loading: true,
      error: '',
    }));

    getListingOffers(listingId)
      .then((offers) => {
        if (!ignore) {
          setListingOffersState({
            offers: Array.isArray(offers) ? offers : [],
            loading: false,
            error: '',
          });
        }
      })
      .catch((err) => {
        if (!ignore) {
          setListingOffersState({
            offers: [],
            loading: false,
            error: err?.message || 'Unable to load deals right now.',
          });
        }
      });

    return () => {
      ignore = true;
    };
  }, [listing?.id, initialOffers]);

  useEffect(() => {
    if (!isNavigationLocked) {
      return undefined;
    }

    const handleDocumentClickCapture = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const link = event.target.closest('a');
      if (!link) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener('click', handleDocumentClickCapture, true);
    return () => document.removeEventListener('click', handleDocumentClickCapture, true);
  }, [isNavigationLocked]);

  useEffect(() => {
    if (!isVerifyContactInfoGuideActive || loading) return;
    const el = document.getElementById('ContactCard');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isVerifyContactInfoGuideActive, loading]);

  const isAddBusinessDescriptionGuideActive = isEmbedPreview
    && guideStepKey === 'add_business_description'
    && (guideStepStatus === 'in_progress' || guideStepStatus === 'started');

  useEffect(() => {
    if (!isAddBusinessDescriptionGuideActive || loading) return;
    const el = document.getElementById('AboutUsCard');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isAddBusinessDescriptionGuideActive, loading]);

  const canEditCurrentListing = useMemo(() => {
    if (isEditorEmbed) return editorCapability.allowed;
    return !isEmbedPreview && canEditListingFromProfile(profile, listing);
  }, [editorCapability.allowed, isEditorEmbed, isEmbedPreview, listing, profile]);
  const listingDetailDeals = useMemo(
    () => mapListingDetailOffers(listingOffersState.offers),
    [listingOffersState.offers],
  );
  const {
    primary: primaryGuide,
    isLoading: guidesLoading,
    error: guidesError,
    refresh: refreshGuides,
  } = useGuides({
    routeKey: 'listing.public.detail',
    pathname: `/listings/${categorySlug || ''}/${slug || ''}`,
    listingId: listing?.id,
    max: 4,
  }, {
    enabled: canEditCurrentListing && Boolean(listing?.id),
  });

  const openListingFieldController = useCallback(({ fieldKey, actionKey, guideInstanceId, stepKey } = {}) => {
    if (!listing?.id) {
      return;
    }

    if (fieldKey === 'listing.category') {
      const categorySelection = getListingCategorySelection(listing);
      openOverlay({
        key: 'listing-category-controller',
        title: 'Edit categories',
        description: 'Choose where this listing appears in the directory.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingCategoryEditor
            listingId={listing.id}
            currentPrimaryCategoryId={categorySelection.primaryCategoryId}
            currentSecondaryCategoryIds={categorySelection.secondaryCategoryIds}
            tier={getTierKey(listing)}
            onCancel={closeOverlay}
            onSaved={async (result) => {
              if (guideInstanceId && stepKey) {
                await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
              }
              const categories = Array.isArray(result?.categories) ? result.categories : [];
              setListing((current) => ({
                ...(current || {}),
                listingCategories: categories.map((category) => ({
                  tenantCategoryId: category.id,
                  tenantCategory: category,
                })),
                category: categories[0]?.name || current?.category || '',
                primaryCategory: categories[0]?.name || current?.primaryCategory || '',
              }));
              await refreshGuides?.();
              closeOverlay();
            }}
          />
        ),
      });
      return;
    }

    if (fieldKey === 'listing.address') {
      openOverlay({
        key: 'listing-address-controller',
        title: 'Edit business address',
        description: 'Update the street address, city, state, and ZIP code for this listing.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingAddressEditor
            listing={listing}
            onCancel={closeOverlay}
            onSaved={async ({ updatedListing, address, values }) => {
              if (guideInstanceId && stepKey) {
                await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
              }
              setListing((current) => ({
                ...(current || {}),
                ...(updatedListing || {}),
                address,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                postalCode: values.postalCode,
              }));
              await refreshGuides?.();
              closeOverlay();
            }}
          />
        ),
      });
      return;
    }

    if (fieldKey === 'listing.phone') {
      openOverlay({
        key: 'listing-phone-controller',
        title: 'Edit phone number',
        description: 'Update the phone number customers should call for this listing.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingPhoneEditor
            listing={listing}
            onCancel={closeOverlay}
            onSaved={async ({ updatedListing, phone }) => {
              if (guideInstanceId && stepKey) {
                await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
              }
              setListing((current) => ({
                ...(current || {}),
                ...(updatedListing || {}),
                phone,
              }));
              await refreshGuides?.();
              closeOverlay();
            }}
          />
        ),
      });
      return;
    }

    if (
      fieldKey === 'listing.contact'
      || fieldKey === 'listing.website'
      || fieldKey === 'listing.websiteUrl'
      || fieldKey === 'listing.email'
    ) {
      openOverlay({
        key: 'listing-contact-controller',
        title: 'Edit contact',
        description: 'Update the phone number, website, and email shown on this listing.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingContactController
            listing={listing}
            guideInstanceId={guideInstanceId}
            stepKey={stepKey}
            onClose={closeOverlay}
            onSaved={async ({ updatedListing, nextContact }) => {
              setListing((current) => ({
                ...(current || {}),
                ...(updatedListing || {}),
                phone: updatedListing?.phone ?? nextContact.phone,
                websiteUrl: updatedListing?.websiteUrl ?? nextContact.websiteUrl,
                website: updatedListing?.websiteUrl ?? nextContact.websiteUrl,
                email: updatedListing?.email ?? nextContact.email,
              }));
              await refreshGuides?.();
            }}
          />
        ),
      });
      return;
    }

    if (
      fieldKey === 'listing.social'
      || fieldKey === 'listing.socialLinks'
      || fieldKey === 'listing.socialLinksJson'
    ) {
      openOverlay({
        key: 'listing-social-controller',
        title: 'Edit social',
        description: 'Update the social profiles shown on this listing.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingSocialController
            listing={listing}
            guideInstanceId={guideInstanceId}
            stepKey={stepKey}
            onClose={closeOverlay}
            onSaved={async ({ updatedListing, nextSocialLinks }) => {
              setListing((current) => ({
                ...(current || {}),
                ...(updatedListing || {}),
                socialLinksJson: updatedListing?.socialLinksJson ?? nextSocialLinks,
                socialLinks: updatedListing?.socialLinksJson ?? nextSocialLinks,
              }));
              await refreshGuides?.();
            }}
          />
        ),
      });
      return;
    }

    if (fieldKey === 'listing.description') {
      openOverlay({
        key: 'listing-about-controller',
        title: 'Edit About Us',
        description: 'Update the business overview shown on this listing.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingAboutController
            listing={listing}
            guideInstanceId={guideInstanceId}
            stepKey={stepKey}
            onClose={closeOverlay}
            onSaved={async ({ updatedListing, nextDescription }) => {
              setListing((current) => ({
                ...(current || {}),
                ...(updatedListing || {}),
                description: updatedListing?.description ?? nextDescription,
              }));
              await refreshGuides?.();
            }}
          />
        ),
      });
      return;
    }

    if (fieldKey === 'listing.hours' || fieldKey === 'listing.businessHours') {
      openOverlay({
        key: 'listing-business-hours-controller',
        title: 'Edit business hours',
        description: 'Update the weekly open and close times shown on this listing.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingBusinessHoursController
            listing={listing}
            guideInstanceId={guideInstanceId}
            stepKey={stepKey}
            onClose={closeOverlay}
            onSaved={async ({ updatedListing, nextBusinessHours }) => {
              setListing((current) => ({
                ...(current || {}),
                ...(updatedListing || {}),
                businessHours: updatedListing?.businessHours ?? nextBusinessHours,
                hours: {
                  ...(current?.hours || {}),
                  ...(updatedListing?.hours || {}),
                  weekly: updatedListing?.hours?.weekly ?? updatedListing?.businessHours ?? nextBusinessHours,
                },
              }));
              await refreshGuides?.();
            }}
          />
        ),
      });
      return;
    }

    if (fieldKey === 'listing.photos' || fieldKey === 'listing.galleryImages' || actionKey === 'listing.photos.add') {
      openOverlay({
        key: 'listing-photos-controller',
        title: 'Edit photos',
        description: 'Add photos to the listing gallery.',
        desktopPresentation: 'sideSheet',
        priority: 'high',
        blocksFab: true,
        closeOnRouteChange: true,
        content: (
          <ListingPhotosController
            listing={listing}
            guideInstanceId={guideInstanceId}
            stepKey={stepKey}
            onClose={closeOverlay}
            onSaved={async ({ addedAssets }) => {
              const addedUrls = (Array.isArray(addedAssets) ? addedAssets : [])
                .map(resolveMediaAssetUrl)
                .filter(Boolean);
              if (addedUrls.length > 0) {
                setListing((current) => ({
                  ...(current || {}),
                  galleryImages: [
                    ...(Array.isArray(current?.galleryImages) ? current.galleryImages : []),
                    ...addedUrls,
                  ],
                }));
              }
              await refreshGuides?.();
            }}
          />
        ),
      });
      return;
    }

    if (fieldKey !== 'listing.name') {
      return;
    }

    openOverlay({
      key: 'listing-name-controller',
      title: 'Edit business name',
      description: 'Update the business name shown on this listing.',
      desktopPresentation: 'sideSheet',
      priority: 'high',
      blocksFab: true,
      closeOnRouteChange: true,
      content: (
        <ListingNameController
          listing={listing}
          guideInstanceId={guideInstanceId}
          stepKey={stepKey}
          onClose={closeOverlay}
          onSaved={async ({ updatedListing, nextName }) => {
            setListing((current) => ({
              ...(current || {}),
              ...(updatedListing || {}),
              name: updatedListing?.name || nextName,
            }));
            await refreshGuides?.();
          }}
        />
      ),
    });
  }, [closeOverlay, listing, openOverlay, refreshGuides, setListing]);

  if (loading) {
    return (
      <section className="py-0 sm:py-6">
        <PublicContainer>
          <Card className="p-6 text-sm" style={{ color: 'var(--page-body-color)', backgroundColor: 'var(--card)' }}>
            Loading listing...
          </Card>
        </PublicContainer>
      </section>
    );
  }

  if (error?.status === 404 || (!error && !listing)) return <NotFoundPage resource="business listing" />;

  if (error) {
    return (
      <section className="py-0 sm:py-6">
        <PublicContainer>
          <Card className="p-6 space-y-3" style={{ backgroundColor: 'var(--card)' }}>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--page-heading-color)' }}>Listings are temporarily unavailable</h1>
            <InlineFeedback variant="error" className="text-sm" style={{ color: 'var(--page-body-color)' }}>
              {error.message}
            </InlineFeedback>
            <div>
              <Link
                to="/listings"
                className="inline-flex h-9 items-center justify-center rounded-lg bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)]"
              >
                Back to Directory
              </Link>
            </div>
          </Card>
        </PublicContainer>
      </section>
    );
  }

  const previewListing = previewTier
    ? {
      ...listing,
      tier: previewTier,
      tierInfo: {
        ...(listing?.tierInfo || {}),
        key: previewTier,
        name: PREVIEW_TIER_LABELS[previewTier] || previewTier,
      },
    }
    : listing;
  const tierKey = getTierKey(previewListing);
  const TierComponent = TIER_COMPONENTS[tierKey] || BasicTierSkeleton;
  const phoneField = previewListing?.phone ? 'listing.phone' : previewListing?.phoneNumber ? 'listing.phoneNumber' : 'fallback';
  const phone = String(previewListing?.phone || previewListing?.phoneNumber || '').trim();
  const addressField = previewListing?.address ? 'listing.address' : previewListing?.addressLine1 ? 'listing.addressLine1' : 'fallback';
  const nameField = previewListing?.name ? 'listing.name' : 'fallback';
  const address = String(previewListing?.address || previewListing?.addressLine1 || '').trim();
  const listingName = String(previewListing?.name || '').trim();
  const claimListingIdentifier = previewListing?.slug || slug || previewListing?.id;
  const claimPath = (() => {
    const publicPath = String(previewListing?.publicUrl || previewListing?.publicPath || '').trim();
    if (publicPath && publicPath.startsWith('/') && !publicPath.startsWith('//')) {
      return `${publicPath.replace(/\/$/, '')}/claim`;
    }
    if (categorySlug && slug) {
      return `/listings/${encodeURIComponent(categorySlug)}/${encodeURIComponent(slug)}/claim`;
    }
    if (slug) {
      return `/listings/${encodeURIComponent(slug)}/claim`;
    }
    if (claimListingIdentifier) {
      return `/listings/${encodeURIComponent(String(claimListingIdentifier))}/claim`;
    }
    return '/claim/new';
  })();
  const directionsQuery = address || listingName;
  const directionsField = address ? addressField : nameField;
  const isVerifyBusinessNameGuideActive = isEmbedPreview
    && guideStepKey === 'verify_business_name'
    && (guideStepStatus === 'in_progress' || guideStepStatus === 'started');
  const previewHotspot = isVerifyBusinessNameGuideActive
    ? {
      target: 'businessName',
      placement: 'center-end',
      offsetPx: 8,
      label: 'Set your business name',
    }
    : null;

  return (
    <section className="py-0 pb-20 sm:py-6 sm:pb-6" onClick={event => {
      if (event.defaultPrevented || isEmbedPreview || isEditorEmbed || previewTier || isNavigationLocked || !listing?.id) return;
      const action = event.target.closest?.('[data-listing-action]');
      if (!action || !event.currentTarget.contains(action) || action.closest('[data-content-listing-card]')) return;
      const eventType = { phone: 'listing.phone.clicked', website: 'listing.website.clicked' }[action.dataset.listingAction];
      if (eventType) trackVisitorEvent({ eventType, listingId: listing.id, metadata: { context: 'business_profile' } });
    }}>
      <PublicContainer>
        {!isEmbedPreview && !isEditorEmbed && !previewTier && publicContent?.breadcrumbs ? <PublicBreadcrumbs items={publicContent.breadcrumbs} /> : null}
        {isEditorEmbed && editorCapability.loading ? (
          <div role="status" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Checking edit access…
          </div>
        ) : null}
        {isEditorEmbed && editorCapability.error ? (
          <InlineFeedback variant="error" role="alert" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
            {editorCapability.error}
          </InlineFeedback>
        ) : null}
        <TierComponent
          shareData={shareData}
          listing={previewListing}
          tagsPanel={<BusinessTagsPanel listingId={listing.id} scopeKey={tenant?.id || listing.tenantId} preview={isEditorEmbed || isEmbedPreview || listing.status !== 'active' || listing.publicationStatus !== 'PUBLISHED'}/>}
          previewHotspot={previewHotspot}
          forceBusinessNameUntint={isVerifyBusinessNameGuideActive}
          canEditListingInfo={canEditCurrentListing}
          deals={listingDetailDeals}
          dealsLoading={listingOffersState.loading}
          dealsError={listingOffersState.error}
          onEditAbout={() => openListingFieldController({ fieldKey: 'listing.description' })}
          onEditContact={() => openListingFieldController({ fieldKey: 'listing.contact' })}
          onEditCategories={() => openListingFieldController({ fieldKey: 'listing.category' })}
          onEditSocial={() => openListingFieldController({ fieldKey: 'listing.social' })}
          onEditName={() => openListingFieldController({ fieldKey: 'listing.name' })}
          onEditHours={() => openListingFieldController({ fieldKey: 'listing.hours' })}
          onEditPhotos={() => openListingFieldController({ fieldKey: 'listing.photos' })}
          onClaim={() => navigate(claimPath)}
        />
        {!isEmbedPreview && publicContent?.locationFacts.areaServed?.length ? <section aria-labelledby="listing-service-areas-heading" className="mt-6 rounded-xl border bg-white p-6"><h2 id="listing-service-areas-heading" className="text-2xl font-bold">Service areas</h2><ul className="mt-3 flex flex-wrap gap-2">{publicContent.locationFacts.areaServed.map(area => <li key={area} className="rounded-lg bg-slate-50 px-3 py-2">{area}</li>)}</ul></section> : null}
        {!isEmbedPreview && previewListing?.experts?.length ? <section aria-labelledby="listing-experts-heading" className="mt-6 rounded-xl border bg-white p-6"><div className="flex items-center gap-2"><BadgeCheck className="text-[var(--primary)]"/><h2 id="listing-experts-heading" className="text-2xl font-bold">Meet the experts</h2></div><div className="mt-5 grid gap-4 md:grid-cols-2">{previewListing.experts.map((expert) => <Link key={expert.id} to={`/experts/${expert.slug}`} className="rounded-lg border p-4 hover:border-[var(--primary)]"><h3 className="font-bold text-[var(--primary)]">{expert.displayName}</h3>{expert.professionalTitle ? <p className="text-sm font-medium text-slate-600">{expert.professionalTitle}</p> : null}{expert.shortBiography ? <p className="mt-2 line-clamp-2 text-sm text-slate-600">{expert.shortBiography}</p> : null}</Link>)}</div></section> : null}
        {!isEmbedPreview && !isEditorEmbed && !previewTier && listing.status === 'active' && listing.publicationStatus === 'PUBLISHED' ? <ListingRelatedContent listingId={listing.id} tenantId={tenant?.id || listing.tenantId} /> : null}
      </PublicContainer>
      <SingleListingSticky
        phone={phone}
        directionsQuery={directionsQuery}
        phoneField={phoneField}
        directionsField={directionsField}
        navigationLocked={isNavigationLocked}
        onMore={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
      />
      {canEditCurrentListing ? (
        <ListingGuidePanel
          guide={primaryGuide}
          loading={guidesLoading}
          error={guidesError}
          onRefresh={refreshGuides}
          onOpenFieldController={openListingFieldController}
        />
      ) : null}
    </section>
  );
}
